import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Bell,
  Briefcase,
  Building,
  Calendar,
  Check,
  Clock,
  FileCheck,
  Key,
  Megaphone,
  MoreHorizontal,
  Package,
  Receipt,
  Settings,
  Shield,
  Trash2,
  User,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Notification, NotificationCategory } from '../types';
import {
  getRelativeTime,
  NOTIFICATION_CATEGORY_COLORS,
  NOTIFICATION_CATEGORY_LABELS,
} from '../types';
import { stripHtml } from '../utils/stripHtml';

export interface NotificationItemProps {
  notification: Notification;
  onClick?: (notification: Notification) => void;
  onMarkAsRead?: (id: string) => void;
  onDismiss?: (id: string) => void;
  compact?: boolean;
  className?: string;
}

const categoryIcons: Record<NotificationCategory, React.ElementType> = {
  AUTH: Key,
  ACCOUNT: User,
  SECURITY: Shield,
  CRM: Users,
  TENANT: Building,
  HRMS: Briefcase,
  PAYROLL: Banknote,
  LEAVE: Calendar,
  REIMBURSEMENT: Receipt,
  ATTENDANCE: Clock,
  ASSET: Package,
  COMPLIANCE: FileCheck,
  SYSTEM: Settings,
  ANNOUNCEMENT: Megaphone,
};

export function NotificationItem({
  notification,
  onClick,
  onMarkAsRead,
  onDismiss,
  compact = false,
  className,
}: NotificationItemProps) {
  const Icon = categoryIcons[notification.category] || Bell;
  const plainBody = stripHtml(notification.body);
  const timeAgo = getRelativeTime(notification.timestamp);
  const categoryLabel = NOTIFICATION_CATEGORY_LABELS[notification.category];
  const isHighPriority = notification.priority === 'HIGH' || notification.priority === 'URGENT';

  const openNotification = () => {
    if (!notification.read) {
      onMarkAsRead?.(notification.id);
    }
    onClick?.(notification);
  };

  return (
    <div
      className={cn(
        'group relative flex min-w-0 items-start border-b transition-colors last:border-b-0',
        'hover:bg-muted/40 focus-within:bg-muted/40',
        !notification.read && 'bg-primary/[0.035]',
        compact ? 'gap-1 px-3 py-1.5' : 'gap-1 px-3 py-2 sm:px-4 sm:py-2.5',
        className,
      )}
    >
      {!notification.read && (
        <span
          className="absolute inset-y-2.5 left-0 w-0.5 rounded-r bg-primary"
          aria-hidden="true"
        />
      )}

      <button
        type="button"
        onClick={openNotification}
        className={cn(
          'flex min-w-0 flex-1 items-start text-left outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          compact ? 'gap-3 rounded-md px-1 py-2' : 'gap-3 rounded-md px-1 py-2 sm:gap-4',
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-md',
            compact ? 'h-9 w-9' : 'h-10 w-10',
            NOTIFICATION_CATEGORY_COLORS[notification.category],
          )}
          aria-hidden="true"
        >
          <Icon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        </span>

        <span className="min-w-0 flex-1">
          {!notification.read && <span className="sr-only">Unread notification. </span>}

          <span className="flex min-w-0 items-start gap-2">
            <span
              className={cn(
                'min-w-0 flex-1 break-words text-sm leading-5 text-foreground',
                compact && 'line-clamp-2',
                !notification.read && 'font-semibold',
              )}
            >
              {notification.title}
            </span>
          </span>

          <span
            className={cn(
              'mt-0.5 block break-words text-sm leading-5 text-muted-foreground',
              compact && 'line-clamp-2 text-xs leading-[1.125rem]',
            )}
          >
            {plainBody}
          </span>

          <span className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{categoryLabel}</span>
            <span aria-hidden="true">&#183;</span>
            <span>{timeAgo}</span>
            {isHighPriority && (
              <span className="inline-flex items-center gap-1 font-medium text-destructive">
                <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                {notification.priority === 'URGENT' ? 'Urgent' : 'High priority'}
              </span>
            )}
            {!compact && notification.actionUrl && (
              <span className="inline-flex items-center gap-1 font-medium text-primary">
                {notification.actionLabel || 'Open'}
                <ArrowRight className="h-3 w-3" aria-hidden="true" />
              </span>
            )}
          </span>
        </span>
      </button>

      {(onMarkAsRead || onDismiss || notification.actionUrl) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                'mt-1 shrink-0 text-muted-foreground hover:text-foreground',
                'data-[state=open]:bg-accent data-[state=open]:text-foreground',
                compact ? 'h-8 w-8' : 'h-9 w-9',
              )}
              aria-label={`Actions for ${notification.title}`}
              title={`Actions for ${notification.title}`}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="z-[100] w-52">
            {!notification.read && onMarkAsRead && (
              <DropdownMenuItem onSelect={() => onMarkAsRead(notification.id)}>
                <Check className="mr-2 h-4 w-4" />
                Mark as read
              </DropdownMenuItem>
            )}
            {notification.actionUrl && (
              <DropdownMenuItem onSelect={openNotification}>
                <ArrowRight className="mr-2 h-4 w-4" />
                {notification.actionLabel || 'Open notification'}
              </DropdownMenuItem>
            )}
            {onDismiss && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => onDismiss(notification.id)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Dismiss notification
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export default NotificationItem;
